import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Language } from '../../shared/domain/Language'
import type { LoginStatus } from '../../auth/domain/LoginStatus'
import { Affiliate } from '../../shared/domain/Affiliate'
import { Market } from '../../shared/domain/Market'
import { GETBannerInformationResponseType } from '../infrastructure/HTTPMarketingRepository/schemas/banners/banners-json'
import { Banner } from './Banner'
import { BannerSubtypes } from './BannerSubtype'
import { BannerTypes } from './BannerType'

export const ListOfBannerValidation = z.object({
  list: z.instanceof(Banner, { message: '[ListOfBanner] list is required' }).array(),
  market: z.instanceof(Market, { message: '[ListOfBanner] market is required' }),
  language: z.instanceof(Language, { message: '[ListOfBanner] language is required' }),
})

export class ListOfBanner extends Model {
  static create({ list, market, language }: z.infer<typeof ListOfBannerValidation>) {
    ListOfBannerValidation.parse({ list, market, language })
    return new ListOfBanner(list, market, language)
  }

  static empty() {
    return new ListOfBanner([], Market.empty(), Language.empty())
  }

  static fromDatasetOrEmpty({
    list,
    market,
    language,
  }: {
    list: GETBannerInformationResponseType['banners']
    market: Market
    language: Language
  }) {
    try {
      return ListOfBanner.fromDataset({
        list,
        market,
        language,
      })
    } catch {
      return ListOfBanner.empty()
    }
  }

  static fromDataset({
    list,
    market,
    language,
  }: {
    list: GETBannerInformationResponseType['banners']
    market: Market
    language: Language
  }) {
    // Filter out invalid banners by attempting to create each one individually
    const validBanners = list
      .map(item => {
        try {
          return Banner.fromDataset({ info: item, market, language })
        } catch {
          // Skip invalid banners with hero banner exception
          if (item.type === 'hero-banner') {
            return Banner.createEmptyHeroBanner(market, language)
          }
          return null
        }
      })
      .filter((banner): banner is Banner => banner !== null)

    return ListOfBanner.create({
      list: validBanners,
      market,
      language,
    })
  }

  static fromJSON(json: ReturnType<ListOfBanner['toJSON']>) {
    return new ListOfBanner(
      json.list.map(item =>
        Banner.fromJSON(item, Market.fromJSON(json.market), Language.fromJSON(json.language))
      ),
      Market.fromJSON(json.market),
      Language.fromJSON(json.language)
    )
  }

  static fromJSONOrEmpty(json: ReturnType<ListOfBanner['toJSON']>) {
    try {
      return ListOfBanner.fromJSON(json)
    } catch {
      return ListOfBanner.empty()
    }
  }

  constructor(
    private readonly _list: Banner[],
    private readonly _market: Market,
    private readonly _language: Language
  ) {
    super()
  }

  get list() { return this._list } // prettier-ignore

  get market() { return this._market } // prettier-ignore
  get language() { return this._language } // prettier-ignore

  getBannerByType(type: BannerTypes) {
    return this._list.find(bannerInfo => bannerInfo.getType() === type)
  }

  getBannerBySubtype(subtype: BannerSubtypes) {
    return this._list.find(bannerInfo => bannerInfo.getSubtype() === subtype)
  }

  getBillboardBannerInformation({
    market,
    affiliate,
    loginStatus,
    isMobile,
  }: {
    market?: Market
    affiliate?: Affiliate
    loginStatus?: LoginStatus
    isMobile: boolean
  }): ReturnType<Banner['getBillboardBannerInformation']> {
    const campaignBanner = this.getBannerBySubtype('campaign')

    if (campaignBanner) {
      return campaignBanner.getBillboardBannerInformation()
    }

    if (
      isMobile &&
      !market?.isOneOf(['es', 'us']) &&
      loginStatus?.isUnlogged() &&
      affiliate?.isCug()
    ) {
      const memberBanner = this.getBannerBySubtype('member')

      return memberBanner?.getBillboardBannerInformation()
    }

    return this.getBannerBySubtype('brand')?.getBillboardBannerInformation()
  }

  toJSON() {
    return {
      list: this._list.map(item => item.toJSON()),
      market: this._market.toJSON(),
      language: this._language.toJSON(),
    }
  }
}
